import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import AutocompleteWithQuery from "components/AutocompleteWithQuery";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  AdminListRequestOfProductFilterV2,
  ListRequestOfBaseFilter,
  ListResponseOfProductType,
  Product,
  ProductFilterV2,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { SnackbarMessageProps } from "types/common-types";

interface ProductWithId extends Product {
  id: string;
}

const useStyles = (theme: Theme) => ({
  button: {
    marginLeft: theme.spacing(1),
  },
});

const ProductList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const styles = useStyles(useTheme());
  const {
    productService,
    productDetailsUrl,
    productCreateUrl,
    productVersionsManagementUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.productFilterSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const productTypeId = persistanceStore.useState((s) => s.productFilterTypeId);

  const { isError, data, error, isFetching } = useQuery(
    [
      "products",
      debouncedSearchTerm,
      page,
      rowsPerPage,
      orderBy,
      order,
      productTypeId,
      organizationId,
    ],
    () =>
      productService.getProductsV2(
        AdminListRequestOfProductFilterV2.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          organizationId,
          parameters: ProductFilterV2.fromJS({
            searchText:
              debouncedSearchTerm !== "" ? debouncedSearchTerm : undefined,
            productTypeId,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const fetchedProductTypes = useQuery<ListResponseOfProductType>(
    "productTypes",
    () =>
      productService.getProductTypes(
        new ListRequestOfBaseFilter({ sortField: "name" })
      )
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, productTypeId, organizationId]);

  useListTitle("menubarProducts");

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const onManageVersions = () => {
    navigate(productVersionsManagementUrl());
  };

  const products = data?.data as ProductWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "title",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "productTitleLabel" }),
    },
    {
      accessor: "productType.name",
      align: "center",
      width: 0,
      type: "text",
      label: t({ id: "typeLabel" }),
      noSort: true,
    },
  ];

  const onClick = (id: string) => {
    navigate(productDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={() => navigate(productCreateUrl())}
        >
          <AddIcon />
        </Fab>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.productFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.productFilterSearchTerm = e.target.value;
                })
              }
              data-cy="searchText"
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <AutocompleteWithQuery
              value={productTypeId}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.productFilterTypeId = id;
                })
              }
              labelField="name"
              fetchedData={fetchedProductTypes}
              failMessage={t({
                id: "selectProductTypeFetchFailed",
              })}
              textFieldProps={{
                variant: "outlined",
              }}
              label={t({ id: "typeLabel" })}
            />
          </Grid>
        </Grid>
      </Box>

      <Box textAlign="right" mb={1}>
        <Button
          sx={styles.button}
          size="small"
          variant="outlined"
          onClick={onManageVersions}
          data-cy="manageVersionsButton"
        >
          <T id="productManageVersionsLink" />
        </Button>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: products ?? [],
          loading: isFetching,

          dense: true,
          title: t({ id: "menubarProducts" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => productDetailsUrl(id)),
          ],
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ProductList;
